import { useRef, useState } from 'react';

import { Surface } from '@kilat/ui';
import { useI18n } from '../lib/use-i18n.js';
import { logger } from '../lib/logger.js';
import { uploadFile, logoSourcePath } from '../lib/storage-upload.js';
import { callBrandingRegenerate } from '../lib/callables/branding.js';

/**
 * The operator's app icon — what a customer taps on their home screen.
 *
 * Missing entirely until 2026-08-21. The pipeline behind it worked, the upload
 * helper and the callable wrapper both existed, and there was simply no control
 * anywhere in the dashboard — so an operator could not change their icon, and a
 * tenant who had never uploaded a logo shipped `iconSet: []`, which installs as
 * a blank tile on the one surface where their brand is the whole point.
 *
 * Two paths, because both are legitimate:
 *   - **Upload a logo** — what an operator with artwork wants.
 *   - **Use a generated icon** — a monogram on their own primary colour, which
 *     is what every tenant now starts with. Offered as a real choice rather
 *     than only a fallback, so an operator who dislikes their upload can get
 *     back to a clean default without hunting for a file.
 */

/** Bigger than any sane logo; the pipeline downscales everything anyway. */
const MAX_BYTES = 5 * 1024 * 1024;
const ACCEPT = 'image/png,image/jpeg,image/webp,image/svg+xml';

type Status =
  | { readonly kind: 'idle' }
  | { readonly kind: 'working'; readonly what: 'upload' | 'generate' }
  | { readonly kind: 'done'; readonly version: number }
  | { readonly kind: 'error'; readonly messageKey: string };

export function AppIconCard({
  tenantId,
  iconSrc,
  brandingVersion,
}: {
  readonly tenantId: string;
  /** Current `any`-purpose icon path, or null when the tenant has none yet. */
  readonly iconSrc: string | null;
  readonly brandingVersion: number;
}) {
  const { t } = useI18n();
  const fileRef = useRef<HTMLInputElement>(null);
  const [status, setStatus] = useState<Status>({ kind: 'idle' });
  const busy = status.kind === 'working';

  async function run(what: 'upload' | 'generate', file?: File) {
    setStatus({ kind: 'working', what });
    try {
      let sourcePath: string | undefined;
      if (file) {
        if (file.size > MAX_BYTES) {
          setStatus({ kind: 'error', messageKey: 'dashboard.branding.icon.errTooBig' });
          return;
        }
        sourcePath = await uploadFile(logoSourcePath(tenantId, file), file);
      }
      const result = await callBrandingRegenerate({
        tenantId,
        ...(sourcePath !== undefined ? { sourcePath } : {}),
      });
      setStatus({ kind: 'done', version: result.brandingVersion });
    } catch (e) {
      logger.error('app icon update failed', { tenantId, what, error: String(e) });
      setStatus({ kind: 'error', messageKey: 'dashboard.branding.icon.errFailed' });
    } finally {
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  // Cache-busted on brandingVersion so a fresh icon appears immediately rather
  // than after the 1-year immutable cache on the icon file expires.
  const preview = iconSrc === null ? null : `/${tenantId}${iconSrc}?v=${String(brandingVersion)}`;

  return (
    <Surface as="section">
      <header style={{ marginBottom: 'var(--space-3)' }}>
        <h2 style={{ fontSize: 'var(--text-h3)', margin: 0 }}>
          {t('dashboard.branding.icon.title')}
        </h2>
        <p
          style={{
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
            margin: 'var(--space-1) 0 0 0',
          }}
        >
          {t('dashboard.branding.icon.subtitle')}
        </p>
      </header>

      <div
        style={{
          display: 'flex',
          gap: 'var(--space-4)',
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        <div
          aria-hidden="true"
          style={{
            width: 88,
            height: 88,
            borderRadius: 20,
            border: '1px solid var(--color-border)',
            background: 'var(--color-surface-2, var(--color-surface))',
            display: 'grid',
            placeItems: 'center',
            overflow: 'hidden',
            flexShrink: 0,
          }}
        >
          {preview === null ? (
            <span style={{ fontSize: 'var(--text-meta)', color: 'var(--color-on-surface-muted)' }}>
              {t('dashboard.branding.icon.none')}
            </span>
          ) : (
            <img src={preview} alt="" width={88} height={88} style={{ display: 'block' }} />
          )}
        </div>

        <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
          <button
            type="button"
            className="k-press"
            disabled={busy}
            onClick={() => fileRef.current?.click()}
            style={buttonStyle}
          >
            {status.kind === 'working' && status.what === 'upload'
              ? t('dashboard.branding.icon.uploading')
              : t('dashboard.branding.icon.upload')}
          </button>
          <button
            type="button"
            className="k-press"
            disabled={busy}
            onClick={() => void run('generate')}
            style={ghostStyle}
          >
            {status.kind === 'working' && status.what === 'generate'
              ? t('dashboard.branding.icon.generating')
              : t('dashboard.branding.icon.generate')}
          </button>
          <input
            ref={fileRef}
            type="file"
            accept={ACCEPT}
            hidden
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void run('upload', file);
            }}
          />
        </div>
      </div>

      {/* One live region for every outcome — never a silent transition. */}
      <p
        role="status"
        aria-live="polite"
        style={{
          fontSize: 'var(--text-meta)',
          margin: 'var(--space-3) 0 0 0',
          color: status.kind === 'error' ? 'var(--color-danger)' : 'var(--color-on-surface-muted)',
          minHeight: '1.2em',
        }}
      >
        {status.kind === 'done'
          ? t('dashboard.branding.icon.done')
          : status.kind === 'error'
            ? t(status.messageKey)
            : t('dashboard.branding.icon.hint')}
      </p>
    </Surface>
  );
}

const buttonStyle: React.CSSProperties = {
  padding: '0.6em 1.1em',
  borderRadius: 'var(--radius-2)',
  border: '1px solid transparent',
  background: 'var(--color-primary)',
  color: 'var(--color-primary-fg)',
  fontWeight: 600,
  cursor: 'pointer',
  minHeight: 'var(--touch-target, 44px)',
};

const ghostStyle: React.CSSProperties = {
  ...buttonStyle,
  background: 'transparent',
  color: 'var(--color-on-surface)',
  border: '1px solid var(--color-border)',
};
